import type { ReactNode } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { SpriteIcon } from '@/components/SpriteIcon';

/**
 * The application's replacement for `window.confirm` and `window.alert`.
 *
 * Presentational only: it owns no state and decides nothing. The caller says
 * what to ask, whether the action is running, and what went wrong; this renders
 * it. Most screens will not use this directly — `useConfirm` and `useAlert` wrap
 * it in the imperative calls the old browser-dialog sites want — but it is
 * exported for the cases that already hold the open/pending state themselves.
 *
 * `hideCancel` is what makes it an alert rather than a question: one button,
 * which acknowledges instead of choosing.
 *
 * Built on react-bootstrap's Modal, as `UserFormModal` and `UserDetailsModal`
 * are, so it inherits the theme's modal styling (including dark mode) instead of
 * carrying a private overlay of its own.
 */

export type ConfirmVariant = 'danger' | 'warning' | 'primary' | 'success' | 'info';

/** Glyph shown when the caller does not name one, per variant. */
const DEFAULT_ICON: Record<ConfirmVariant, string> = {
  danger: 'alert-triangle',
  warning: 'alert-triangle',
  primary: 'help-circle',
  success: 'check-circle',
  info: 'info',
};

export interface ConfirmDialogProps {
  /** Defaults to true: the common case is mounting the dialog only while asking. */
  show?: boolean;
  title?: ReactNode;
  /** The question. A node, not just a string, so callers can bold a name or list what is affected. */
  message: ReactNode;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  /** Colours the icon and the confirm button. `danger` for anything destructive. */
  variant?: ConfirmVariant;
  /** Name of a glyph in the shared sprite; overrides the variant's default. */
  icon?: string;
  /** While true the buttons are disabled, the confirm button spins, and the dialog cannot be dismissed. */
  pending?: boolean;
  /** Shown above the buttons when the confirmed action failed. */
  error?: ReactNode;
  /**
   * Bootstrap's modal widths. Left unset the dialog is the standard 500px —
   * `sm` is 300px, which is too narrow for a sentence naming what is affected.
   * Pass `lg` when the message lists rows rather than asking about one.
   */
  size?: 'sm' | 'lg' | 'xl';
  /** Drops the cancel button, leaving a single acknowledging one: an alert, not a question. */
  hideCancel?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmDialog({
  show = true,
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = 'danger',
  icon,
  pending = false,
  error,
  size,
  hideCancel = false,
  onCancel,
  onConfirm,
}: ConfirmDialogProps) {
  const { t } = useTranslation();

  const heading = title ?? (hideCancel ? null : t('Message.Confirm', 'Are you sure?'));

  return (
    <Modal
      show={show}
      // Backdrop click and Escape both cancel, unless the action is already
      // running — dismissing then would leave the caller's promise unsettled
      // while the request is still in flight.
      onHide={() => {
        if (!pending) onCancel();
      }}
      backdrop={pending ? 'static' : true}
      keyboard={!pending}
      centered
      size={size}
      aria-labelledby="confirm-dialog-title"
    >
      <Modal.Body className="text-center p-4">
        <div
          className={`d-inline-flex align-items-center justify-content-center rounded-circle mb-3 bg-${variant}-subtle text-${variant}-emphasis`}
          style={{ width: '3.5rem', height: '3.5rem' }}
        >
          {/* `sa-current` is what makes the glyph take the variant colour from
              the wrapper: `.sa-icon` otherwise paints a fixed grey. */}
          <SpriteIcon
            name={icon ?? DEFAULT_ICON[variant]}
            size={26}
            className="sa-icon sa-medium sa-current sa-nofill"
          />
        </div>

        {/* "Are you sure?" is the fallback for a question only. An alert without
            a title of its own is simply the message, which then names the
            dialog. */}
        {heading && (
          <h5 className="mb-2" id="confirm-dialog-title">
            {heading}
          </h5>
        )}
        <div className="text-muted mb-0" id={heading ? undefined : 'confirm-dialog-title'}>
          {message}
        </div>

        {error && (
          <div className="alert alert-danger mt-3 mb-0 text-start" role="alert">
            {error}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0 justify-content-center">
        {!hideCancel && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={pending}
            // Focus starts on the way out, not on the destructive button: Enter
            // on an unread dialog should cancel. An alert has no way out to
            // start on, so there the single button takes it.
            autoFocus
          >
            {/* Button.* and Message.* are the keys the Razor views already had
                translated in shared.json; there is nothing new to translate. */}
            {cancelLabel ?? t('Button.Cancel', 'Cancel')}
          </button>
        )}
        <button
          type="button"
          className={`btn btn-${variant}`}
          onClick={onConfirm}
          disabled={pending}
          autoFocus={hideCancel}
        >
          {pending && <span className="spinner-border spinner-border-sm me-2" role="status" />}
          {confirmLabel ?? (hideCancel ? t('Button.Ok', 'OK') : t('Button.Confirm', 'Confirm'))}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
